import { createVeriffFrame, MESSAGES } from '@veriff/incontext-sdk'

import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'

import { useMutation } from '@tanstack/react-query'

import { IdCard, CheckCircle, ShieldAlert, Smartphone } from 'lucide-react'

import startPreCheckSessionMutation from './services/start-pre-check-session'
import VerificationCanceledDialog from '../VerificationCanceledDialog'
import Overlay from '../../../../../../Components/Overlay'

// Module-level guards. These deliberately live OUTSIDE the component instance
// so they survive remounts (the parent renders <PreCheck key={documentId} />,
// so React tears the instance down and back up when the document changes) and
// any effect double-invocation. Component-instance refs reset to null on
// remount, which let createVeriffFrame fire a second time against the same
// sessionUrl in production — Veriff then treats the session as opened on
// another device and skips the QR screen.
let activeVeriffFrame = null           // the live Veriff frame, if any

const VerificationComponent = ({
    data,
    onSubmitted,
    onCanceled,
    documentText,
    handleNext,
    formKey,
    refetch,
    documentId,
}) => {

    // Which device the applicant is actually verifying on decides which Veriff event
    // means "done". When they continue on their phone (the QR flow), the desktop frame
    // never fires STARTED and reports SUBMITTED once the phone submits. When they verify
    // right here on the computer, STARTED fires first and completion comes through as
    // FINISHED (SUBMITTED is just an intermediate step of that same on-computer flow).
    // A ref (not state) so the onEvent closure — created once, keyed on sessionUrl —
    // always reads the latest value.
    const startedOnComputerRef = useRef(false)

    // Set when an on-computer verification is closed before completing (CANCELED); drives
    // the "not completed" dialog that sends the applicant back to the previous step.
    const [showCanceledDialog, setShowCanceledDialog] = useState(false)

    const {
        isPending: startPreCheckSessionIsPending,
        mutate: startPreCheckSession,
    } = useMutation({
        mutationFn: startPreCheckSessionMutation,
        onSuccess: async () => {
            await refetch()
        },
    })

    useEffect(() => {
        if (data?.sessionUrl) return
        if (data?.status === 'submitted') return
        startPreCheckSession({
            key: formKey,
            currentPageType: "pre_check",
            currentPageDocumentId: documentId,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleRedirect = useCallback(() => {
        if (onSubmitted) {
            onSubmitted()
        }
    }, [onSubmitted])

    // OK on the "not completed" dialog: dismiss it and send the applicant back to the
    // previous step (the PreCheck intro screen with the physical-document notice).
    const handleCanceledConfirm = useCallback(() => {
        setShowCanceledDialog(false)
        if (onCanceled) {
            onCanceled()
        }
    }, [onCanceled])

    useEffect(() => {
        if (data?.status === "submitted") {
            return
        }

        // A different session became active (e.g. moving to another document) —
        // tear down the previous frame before building the new one.
        if (activeVeriffFrame) {
            activeVeriffFrame.close()
            activeVeriffFrame = null
        }

        if (!data?.sessionUrl) {
            return
        }

        activeVeriffFrame = createVeriffFrame({
            url: data.sessionUrl,
            embedded: true,
            embeddedOptions: {
                rootElementID: 'embedded-container',
            },
            onEvent: function (msg) {
                switch (msg) {
                    case MESSAGES.STARTED:
                        // Only fires when the applicant verifies on this computer; the
                        // phone (QR) flow never reports it. Flags the on-computer path so
                        // completion is taken from FINISHED (not SUBMITTED) below.
                        startedOnComputerRef.current = true
                        break;
                    case MESSAGES.SUBMITTED: {
                        // Phone flow only: this is the completion signal. On the
                        // on-computer flow SUBMITTED is intermediate — wait for FINISHED.
                        if (!startedOnComputerRef.current) {
                            handleRedirect()
                        }
                        break;
                    }
                    case MESSAGES.FINISHED:
                        // On-computer flow completion signal.
                        handleRedirect()
                        break;
                    case MESSAGES.CANCELED:
                        // Applicant closed an on-computer verification before finishing —
                        // nothing was submitted; inform them and send them back a step.
                        setShowCanceledDialog(true)
                        break;
                    case MESSAGES.RELOAD_REQUEST:
                        console.log('Verification reloaded');
                        break;
                    default:
                        break;
                }
            },
        })

        // No cleanup that closes the frame here. Closing on unmount would let
        // the very next mount (from the key={documentId} remount) rebuild a
        // frame for the same sessionUrl — the exact duplicate we are guarding
        // against. The frame is torn down above only when a genuinely new
        // sessionUrl takes over.
        //
        // Intentionally keyed on sessionUrl only. Re-running on `status`
        // changes would inject a duplicate frame for the same session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.sessionUrl])

    const isAlreadySubmitted = data?.status === 'submitted'

    return (
        <div className="flex items-center justify-center px-4 py-10">
            <div className="max-w-3xl w-full bg-white shadow-xl rounded-2xl p-8 space-y-6">
                <div className="space-y-2">
                    <div className="flex items-start justify-between gap-3">
                        <h1 className="text-2xl font-bold text-gray-800">Identity Verification</h1>
                        {!isAlreadySubmitted && (
                            <div className="inline-flex items-center gap-2 rounded-full bg-rose-50 border border-rose-100 px-3 py-1.5 shrink-0">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="absolute inline-flex h-full w-full rounded-full bg-rose-300 opacity-75 animate-ping" />
                                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-400" />
                                </span>
                                <Smartphone className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                                <span className="text-xs font-semibold text-rose-500 whitespace-nowrap">
                                    Recorded live on your phone
                                </span>
                            </div>
                        )}
                    </div>
                    {documentText && (
                        <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 text-sm font-medium px-3 py-1.5 rounded-lg">
                            <IdCard className="w-4 h-4 shrink-0" />
                            {documentText}
                        </div>
                    )}
                    <p className="text-gray-600 text-base">
                        Please complete the identity verification steps below.
                    </p>
                </div>

                {isAlreadySubmitted ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-5 text-center">
                        <CheckCircle className="w-16 h-16 text-green-500" />
                        <div className="space-y-1">
                            <h2 className="text-xl font-semibold text-gray-800">Verification Already Submitted</h2>
                            <p className="text-gray-500 text-sm max-w-sm">
                                Your identity verification has already been submitted.
                            </p>
                            <div className="text-center">
                                <button
                                    onClick={handleNext}
                                    className="px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg shadow-md transition-all disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                                >
                                    Continue
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex items-center justify-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs font-medium text-amber-800 text-center">
                            <ShieldAlert className="w-4 h-4 shrink-0" />
                            Use your original, physical document only — no screen photos, photocopies, or scans.
                        </div>

                        <div className="w-full md:w-[635px] h-auto md:h-[670px] min-h-[670px]" id="embedded-container" />
                    </div>
                )}
            </div>

            {showCanceledDialog && (
                <VerificationCanceledDialog onConfirm={handleCanceledConfirm} />
            )}

            {startPreCheckSessionIsPending && <Overlay />}
        </div>
    )

}

export default VerificationComponent
